import { axiom } from '../../lib/axiom';
import React, { useState } from 'react';
import { Plus, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useStreams } from '../../contexts/StreamsContext';

interface EmptyStreamStateProps {
  streamId: string;
}

export function EmptyStreamState({ streamId }: EmptyStreamStateProps) {
  const { createNode } = useStreams();
  const [taskName, setTaskName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleAddTask = async () => {
    if (!taskName.trim() || !streamId) return;

    setIsCreating(true);
    try {
      await createNode(streamId, taskName.trim(), 150, 150);
      setTaskName('');
    } catch (err) {
      axiom.error('Failed to create task', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsCreating(false);
    }
  };

  return <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-20 h-20 rounded-full bg-surface-800 flex items-center justify-center mb-6 border-2 border-dashed border-surface-500">
        <Plus className="w-8 h-8 text-text-muted" />
      </div>

      <h2 className="text-2xl font-bold text-text-primary mb-2">
        Add your first task
      </h2>
      <p className="text-text-secondary max-w-md mb-8">
        Get started by defining the first step, or let AI help you structure the
        project.
      </p>

      <div className="w-full max-w-md space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="e.g., Research existing solutions..."
            className="flex-1 bg-surface-800 border border-surface-500 rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
            disabled={isCreating}
          />
          <Button
            className="bg-primary-600 hover:bg-blue-700 text-text-primary border-none"
            onClick={handleAddTask}
            disabled={!taskName.trim() || isCreating}
          >
            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </Button>
        </div>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-surface-600"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#0B0F17] px-2 text-sm text-text-muted">or</span>
          </div>
        </div>

        <button
          className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-surface-500 text-text-muted cursor-not-allowed opacity-50"
          disabled
          title="Coming soon"
        >
          <Sparkles className="w-4 h-4 text-teal-400/50" />
          <span>Let AI suggest initial tasks</span>
          <span className="text-xs bg-surface-700 px-2 py-0.5 rounded">Soon</span>
        </button>
      </div>
    </div>;
}